import { useCallback, useMemo } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { can, type Action, type Resource, type Role } from './matrix';

/**
 * `usePermissions` — read the current user's principal from `useAuth()`
 * and return a curried `can(resource, action)` predicate bound to that
 * role.
 *
 * Role resolution today: when the dev-bypass account is active, the role
 * defaults to `'partner'` (matching the dev tenant `dev-tid-partner-a`).
 * Once W1-F6-S4 surfaces real JWT role claims via `account.idTokenClaims`
 * we read from `roles[0]` / a custom `senviro_role` claim (the lookup is
 * tolerant of either shape). This keeps callers stable across the W1
 * → W4 → W10 timeline.
 */

export interface UsePermissionsResult {
  role: Role;
  /** Curried predicate bound to the current user's role. */
  can: (resource: Resource, action: Action) => boolean;
}

const DEFAULT_ROLE: Role = 'partner';

interface IdTokenLikeClaims {
  roles?: ReadonlyArray<string>;
  senviro_role?: string;
}

interface AccountLike {
  idTokenClaims?: IdTokenLikeClaims;
  username?: string;
  homeAccountId?: string;
}

function normalizeRole(value: string | undefined): Role | undefined {
  switch (value) {
    case 'platform':
    case 'partner':
    case 'customer':
      return value;
    default:
      return undefined;
  }
}

export function resolveRoleFromAccount(account: AccountLike | null): Role {
  if (!account) return DEFAULT_ROLE;
  const claims = account.idTokenClaims;
  if (claims) {
    const fromCustom = normalizeRole(claims.senviro_role);
    if (fromCustom) return fromCustom;
    const fromRoles = normalizeRole(claims.roles?.[0]);
    if (fromRoles) return fromRoles;
  }
  return DEFAULT_ROLE;
}

export function usePermissions(): UsePermissionsResult {
  const { account } = useAuth();
  const role = useMemo<Role>(
    () => resolveRoleFromAccount(account as AccountLike | null),
    [account],
  );
  const curried = useCallback(
    (resource: Resource, action: Action): boolean => can(role, resource, action),
    [role],
  );
  return { role, can: curried };
}
